import type { NavEntry, NavNode } from './types';

/**
 * Create an empty root node for tree structure (internal use only)
 */
function createRootNode(): NavNode {
  return {
    name: 'Root',
    path: '/',
    lastVisited: null,
    type: 'generic',
    children: [],
  };
}

/**
 * Find a node anywhere in the tree by path (BFS)
 */
function findNodeByPath(root: NavNode, path: string): NavNode | null {
  const queue: NavNode[] = [root];

  while (queue.length > 0) {
    const node = queue.shift();
    if (!node) continue;

    if (node.path === path) {
      return node;
    }

    queue.push(...node.children);
  }

  return null;
}

/**
 * Insert a single entry into the tree, creating parent nodes as needed
 */
function insertEntryIntoTree(root: NavNode, entry: NavEntry, allEntries: NavEntry[]): NavNode {
  // First, check if this entry already exists anywhere in the tree
  const existingNode = findNodeByPath(root, entry.path);
  if (existingNode) {
    // Update existing node with latest data
    existingNode.name = entry.name;
    existingNode.type = entry.type;
    existingNode.lastVisited = entry.lastVisited;
    existingNode.pinned = entry.pinned ?? false;
    return existingNode;
  }

  let current = root;

  // Build parent chain from stored parent info
  if (entry.parents && entry.parents.length > 0) {
    for (const parentInfo of entry.parents) {
      // Check if parent already exists anywhere in the tree
      let child = findNodeByPath(root, parentInfo.path);

      if (child) {
        // Parent exists - navigate to it
        current = child;
      } else {
        // Parent doesn't exist - check if it's a stored entry
        const parentEntry = allEntries.find((e) => e.path === parentInfo.path);

        if (parentEntry) {
          // Parent exists as entry - recursively insert it first
          child = insertEntryIntoTree(root, parentEntry, allEntries);
          current = child;
        } else {
          // Parent doesn't exist as entry - create placeholder in current location
          child = {
            path: parentInfo.path,
            name: parentInfo.name,
            type: parentInfo.type,
            lastVisited: null,
            children: [],
          };
          current.children.push(child);
          current = child;
        }
      }
    }
  }

  // Create new node for this entry at the current location
  const entryNode: NavNode = {
    path: entry.path,
    name: entry.name,
    type: entry.type,
    lastVisited: entry.lastVisited,
    children: [],
    pinned: entry.pinned ?? false,
  };
  current.children.push(entryNode);

  return entryNode;
}

/**
 * Build a hierarchical tree structure from flat entries
 * Used by RecentPagesTree component for nested rendering
 */
export function buildTreeFromEntries(entries: NavEntry[]): NavNode {
  const root = createRootNode();

  // Track which entries have been processed to avoid duplicates
  const processed = new Set<string>();

  for (const entry of entries) {
    if (!processed.has(entry.path)) {
      insertEntryIntoTree(root, entry, entries);
      processed.add(entry.path);
    }
  }

  return root;
}
